import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Nav } from '../components/icons/NavIcon';
import { useTour } from './tourContext';
import { TOURS, getToursForRoute, getToursByCategory } from './tourContent';

/**
 * HelpLauncher — the always-on entry point into the platform's tour system.
 *
 * Visual: a 56px navy circle pinned bottom-right of the viewport with a
 * white "?" icon and a soft drop shadow (Intercom-class affordance). Click
 * opens a compact panel with three primary actions:
 *   1. Tours for this page — context-aware list keyed off `useLocation()`
 *   2. Browse all tours    — opens an inline modal grouped by category
 *   3. Help center         — placeholder hook for the existing help drawer
 *   4. Contact support     — placeholder until the route is wired
 *
 * Z-order: above page content (`z-50`), below the actual tour spotlight
 * (`z-9000`) so the launcher hides while a tour runs.
 *
 * Keyboard: Esc closes the panel and the modal. Tab order is contained
 * inside the panel; focus returns to the trigger on close.
 */

const ALL_TOURS_CATEGORIES = getToursByCategory();

export default function HelpLauncher() {
  const { startTour, isRunning, completedIds, registerLauncherToggle } = useTour();
  const [panelOpen, setPanelOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Allow the TourProvider to programmatically open the launcher (used by
  // the welcome tour's "Continue exploring →" outro).
  useEffect(() => {
    registerLauncherToggle(setPanelOpen);
    return () => registerLauncherToggle(null);
  }, [registerLauncherToggle]);

  // Keep the launcher button visible while a tour is running so the
  // welcome tour can spotlight it; only the open panel is hidden (the
  // tour reopens it after completion via `openLauncher`). The button
  // can't open a panel while a tour is mid-flight either way.
  const isHidden = false;
  const lockedByTour = isRunning;

  const toursForPage = useMemo(() => {
    if (typeof window === 'undefined') return [];
    return getToursForRoute(window.location.pathname);
  }, [panelOpen]);

  // Esc closes the topmost layer (modal first, then panel).
  useEffect(() => {
    if (!panelOpen && !browseOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (browseOpen) setBrowseOpen(false);
      else setPanelOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen, browseOpen]);

  // Click-outside closes the panel (but not the inner modal).
  useEffect(() => {
    if (!panelOpen || browseOpen) return undefined;
    const onClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setPanelOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [panelOpen, browseOpen]);

  const handleStart = useCallback(
    (tourId) => {
      setPanelOpen(false);
      setBrowseOpen(false);
      startTour(tourId);
    },
    [startTour]
  );

  if (isHidden) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (lockedByTour) return;
          setPanelOpen((v) => !v);
        }}
        aria-label={panelOpen ? 'Close help panel' : 'Open help launcher'}
        aria-expanded={panelOpen}
        aria-disabled={lockedByTour || undefined}
        title="Help & tours"
        data-tour-target="help.launcher"
        className="txnweb-help-launcher-btn"
      >
        {panelOpen ? <Nav.x size={22} aria-hidden /> : <Nav.helpCircle size={22} aria-hidden />}
      </button>

      {panelOpen && !lockedByTour ? (
        <div ref={panelRef} className="txnweb-help-launcher-panel" role="dialog" aria-label="Help & tours">
          <header className="txnweb-help-launcher-panel-head">
            <div>
              <p className="txnweb-help-launcher-panel-eyebrow">Help</p>
              <h3 className="txnweb-help-launcher-panel-title">Welcome to TxnWeb</h3>
            </div>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="txnweb-help-launcher-panel-close"
              aria-label="Close help panel"
            >
              <Nav.x size={16} aria-hidden />
            </button>
          </header>

          {toursForPage.length > 0 ? (
            <section className="txnweb-help-launcher-section">
              <p className="txnweb-help-launcher-section-eyebrow">For this page</p>
              <ul className="txnweb-help-launcher-list">
                {toursForPage.map((tour) => (
                  <li key={tour.id}>
                    <button
                      type="button"
                      onClick={() => handleStart(tour.id)}
                      className="txnweb-help-launcher-tour-row"
                    >
                      <span className="txnweb-help-launcher-tour-icon">
                        <Nav.playCircle size={18} aria-hidden />
                      </span>
                      <span className="txnweb-help-launcher-tour-text">
                        <span className="txnweb-help-launcher-tour-name">
                          {tour.name}
                          {completedIds.includes(tour.id) ? (
                            <Nav.check size={12} className="txnweb-help-launcher-tour-check" aria-label="Tour completed" />
                          ) : null}
                        </span>
                        <span className="txnweb-help-launcher-tour-desc">{tour.description}</span>
                      </span>
                      <Nav.arrowRight size={14} className="txnweb-help-launcher-tour-arrow" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="txnweb-help-launcher-section">
            <p className="txnweb-help-launcher-section-eyebrow">Resources</p>
            <ul className="txnweb-help-launcher-list">
              <li>
                <button
                  type="button"
                  onClick={() => setBrowseOpen(true)}
                  className="txnweb-help-launcher-row"
                >
                  <span className="txnweb-help-launcher-row-icon">
                    <Nav.bookOpen size={16} aria-hidden />
                  </span>
                  <span className="txnweb-help-launcher-row-label">Browse all tours</span>
                  <span className="txnweb-help-launcher-row-meta tabular-nums">{TOURS.length}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleStart('welcome')}
                  className="txnweb-help-launcher-row"
                >
                  <span className="txnweb-help-launcher-row-icon">
                    <Nav.playCircle size={16} aria-hidden />
                  </span>
                  <span className="txnweb-help-launcher-row-label">Take the full platform tour</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setPanelOpen(false);
                  }}
                  className="txnweb-help-launcher-row txnweb-help-launcher-row-disabled"
                  aria-disabled="true"
                  title="Coming soon"
                >
                  <span className="txnweb-help-launcher-row-icon">
                    <Nav.phone size={16} aria-hidden />
                  </span>
                  <span className="txnweb-help-launcher-row-label">Contact support</span>
                  <span className="txnweb-help-launcher-row-meta">Soon</span>
                </button>
              </li>
            </ul>
          </section>

          <footer className="txnweb-help-launcher-panel-foot">
            <span className="tabular-nums">
              {completedIds.length} / {TOURS.length} tours completed
            </span>
          </footer>
        </div>
      ) : null}

      {browseOpen && !lockedByTour ? (
        <BrowseAllToursModal
          onClose={() => setBrowseOpen(false)}
          onStart={handleStart}
          completedIds={completedIds}
        />
      ) : null}
    </>
  );
}

function BrowseAllToursModal({ onClose, onStart, completedIds }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  return (
    <div className="txnweb-help-launcher-modal-root">
      <button
        type="button"
        className="txnweb-help-launcher-modal-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="All product tours"
        tabIndex={-1}
        className="txnweb-help-launcher-modal"
      >
        <header className="txnweb-help-launcher-modal-head">
          <div>
            <p className="txnweb-help-launcher-modal-eyebrow">Help · All tours</p>
            <h2 className="txnweb-help-launcher-modal-title">Pick a walkthrough</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="txnweb-help-launcher-modal-close"
            aria-label="Close"
          >
            <Nav.x size={18} aria-hidden />
          </button>
        </header>
        <div className="txnweb-help-launcher-modal-body">
          {ALL_TOURS_CATEGORIES.map((group) => (
            <section key={group.id} className="txnweb-help-launcher-modal-section">
              <h3 className="txnweb-help-launcher-modal-section-title">{group.label}</h3>
              <div className="txnweb-help-launcher-modal-grid">
                {group.tours.map((tour) => {
                  const done = completedIds.includes(tour.id);
                  return (
                    <button
                      key={tour.id}
                      type="button"
                      onClick={() => onStart(tour.id)}
                      className="txnweb-help-launcher-modal-card"
                    >
                      <span className="txnweb-help-launcher-modal-card-head">
                        <span className="txnweb-help-launcher-modal-card-name">{tour.name}</span>
                        {done ? (
                          <span className="txnweb-help-launcher-modal-card-done" aria-label="Completed">
                            <Nav.check size={12} aria-hidden />
                          </span>
                        ) : null}
                      </span>
                      <span className="txnweb-help-launcher-modal-card-desc">{tour.description}</span>
                      <span className="txnweb-help-launcher-modal-card-meta tabular-nums">
                        {tour.steps.length} steps
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
